import { useState, useEffect } from "react";
import api, { apiErr } from "@/lib/api";
import { uploadArtFile, artUrl } from "@/lib/uploadArt";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { HelpHint } from "@/components/HelpHint";
import { Loader2, FileDown, Wand2 } from "lucide-react";
import { toast } from "sonner";

export const CustomizeTab = () => {
  const [templates, setTemplates] = useState([]);
  const [tpl, setTpl] = useState(null);
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/studio/templates").then((r) => {
      setTemplates((r.data.templates || []).filter((t) => (t.editable_fields || []).length > 0));
    }).catch(() => {});
  }, []);

  const pick = (id) => {
    const t = templates.find((x) => x.id === id) || null;
    setTpl(t); setResult(null);
    const init = {};
    (t?.editable_fields || []).forEach((f) => { if (f.type !== "image") init[f.id] = f.default || ""; });
    setValues(init);
  };

  const setVal = (id, v) => setValues((p) => ({ ...p, [id]: v }));

  const uploadImg = async (id, file) => {
    if (!file) return;
    try { const up = await uploadArtFile(file); setVal(id, { file_id: up.file_id, name: file.name }); toast.success("Image added"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const generate = async () => {
    if (!tpl) return;
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/studio/render-customizable", { template_id: tpl.id, values });
      setResult(r.data); toast.success("Print-ready PDF generated");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="customize-tab">
      <p className="text-sm text-slate-500 flex items-center gap-2">Personalize one of your designs and export it print-ready.
        <HelpHint side="right">Pick a design template, then fill only the editable fields (text, logo, color, QR). Your locked artwork stays intact and the output is a 300 dpi CMYK PDF with bleed and no guides.</HelpHint>
      </p>

      <div>
        <label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">Design template</label>
        <select className="w-full h-10 rounded-lg border px-2 text-sm" data-testid="cust-template-select" value={tpl?.id || ""} onChange={(e) => pick(e.target.value)}>
          <option value="">Select a design…</option>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name} — {(t.editable_fields || []).length} field(s)</option>)}
        </select>
      </div>

      {tpl && (
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            {tpl.background_file_id
              ? <img src={artUrl(tpl.background_file_id)} alt="design" className="w-full rounded-lg border" data-testid="cust-preview" />
              : <p className="text-xs text-slate-400">This template has no background image.</p>}
          </div>
          <div className="space-y-3" data-testid="cust-fields">
            {(tpl.editable_fields || []).map((f) => (
              <div key={f.id}>
                <label className="text-[11px] font-mono uppercase text-slate-500 mb-1 block">{f.label || f.id} <span className="text-slate-300">({f.type})</span></label>
                {f.type === "image"
                  ? <label className="inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-xs cursor-pointer hover:border-[#2495D3]" data-testid={`cust-${f.id}-img`}>
                      {values[f.id]?.name || "Upload image / logo"}
                      <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => { uploadImg(f.id, e.target.files?.[0]); e.target.value = ""; }} />
                    </label>
                  : f.type === "color"
                  ? <input type="color" className="h-10 w-16" value={values[f.id] || f.color || "#000000"} onChange={(e) => setVal(f.id, e.target.value)} data-testid={`cust-${f.id}-color`} />
                  : <Input value={values[f.id] ?? ""} placeholder={f.type === "qr" ? "URL or text for the QR" : "Your text"} onChange={(e) => setVal(f.id, e.target.value)} data-testid={`cust-${f.id}-input`} />}
              </div>
            ))}
            <Button onClick={generate} disabled={busy} data-testid="cust-generate">
              {busy ? <><Loader2 size={16} className="animate-spin" /> Working…</> : <><Wand2 size={16} /> Build print-ready PDF</>}
            </Button>
          </div>
        </div>
      )}

      {result && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 flex items-center justify-between" data-testid="cust-result">
          <div className="text-sm"><b>{result.filename}</b><div className="text-xs text-slate-500">300 dpi CMYK · guides removed</div></div>
          <a href={artUrl(result.file_id)} target="_blank" rel="noreferrer" download>
            <Button size="sm" data-testid="cust-download"><FileDown size={16} /> Download</Button>
          </a>
        </div>
      )}
    </div>
  );
};

export default CustomizeTab;
